import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, AlertCircle, AlertTriangle, X, ChevronRight, Settings } from 'lucide-react';
import { useAlerts } from '@/hooks/useAlerts';
import type { Alert } from '@/services/alertService';

export const NotificationCenter: React.FC = () => {
  const navigate = useNavigate();
  const { alerts, stats, markAsRead, markAllAsRead, dismissAlert } = useAlerts();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fermer le dropdown si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Bloquer le scroll du body quand le modal est ouvert sur mobile
  useEffect(() => {
    if (isOpen && window.innerWidth < 640) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleAlertClick = (alert: Alert) => {
    markAsRead(alert.id);
    setIsOpen(false);
    navigate('/alerts');
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    return `Il y a ${days}j`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bouton de notification */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors"
        aria-label="Notifications"
      >
        <Bell
          size={20}
          className={stats.critical > 0 ? 'text-red-500' : 'text-gray-600'}
        />
        {stats.unread > 0 && (
          <span className={`
            absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px]
            flex items-center justify-center
            text-[10px] font-bold text-white rounded-full px-1
            ${stats.critical > 0 ? 'bg-red-500 animate-pulse' : 'bg-primary'}
          `}>
            {stats.unread > 99 ? '99+' : stats.unread}
          </span>
        )}
      </button>

      {/* Overlay mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 sm:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Dropdown / Modal */}
      {isOpen && (
        <div className={`
          bg-white shadow-xl border overflow-hidden z-50
          /* Mobile: Bottom sheet */
          fixed bottom-0 left-0 right-0 rounded-t-2xl max-h-[80vh]
          /* Desktop: Dropdown */
          sm:absolute sm:bottom-auto sm:left-auto sm:right-0 sm:mt-2
          sm:w-96 sm:rounded-xl sm:max-h-[500px]
        `}>
          {/* Header */}
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between sticky top-0">
            <div>
              <h3 className="font-semibold text-gray-900">Notifications</h3>
              <p className="text-xs text-gray-500">
                {stats.total === 0
                  ? 'Aucune alerte'
                  : `${stats.unread} non lue${stats.unread > 1 ? 's' : ''}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {stats.unread > 0 && (
                <button
                  onClick={() => markAllAsRead()}
                  className="text-xs text-primary hover:text-primary-dark font-medium whitespace-nowrap"
                >
                  Tout marquer lu
                </button>
              )}
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/settings');
                }}
                className="p-1.5 hover:bg-gray-200 rounded-lg"
                title="Paramètres"
              >
                <Settings size={16} className="text-gray-500" />
              </button>
              {/* Bouton fermer visible uniquement sur mobile */}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-gray-200 rounded-lg sm:hidden"
              >
                <X size={18} className="text-gray-500" />
              </button>
            </div>
          </div>

          {/* Liste des alertes */}
          <div className="overflow-y-auto max-h-[60vh] sm:max-h-80">
            {alerts.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell size={32} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500 text-sm">Aucune alerte</p>
                <p className="text-gray-400 text-xs">
                  Vos stocks sont à des niveaux acceptables
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {alerts.slice(0, 10).map((alert) => (
                  <AlertItem
                    key={alert.id}
                    alert={alert}
                    onClick={() => handleAlertClick(alert)}
                    onDismiss={() => dismissAlert(alert.id)}
                    formatTime={formatTime}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {alerts.length > 0 && (
            <div className="px-4 py-3 border-t bg-gray-50 safe-area-bottom">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/alerts');
                }}
                className="w-full text-center text-sm text-primary hover:text-primary-dark font-medium flex items-center justify-center gap-1 py-1"
              >
                Voir toutes les alertes
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Composant pour chaque alerte
interface AlertItemProps {
  alert: Alert;
  onClick: () => void;
  onDismiss: () => void;
  formatTime: (date: Date) => string;
}

const AlertItem: React.FC<AlertItemProps> = ({
  alert,
  onClick,
  onDismiss,
  formatTime,
}) => {
  const isCritical = alert.type === 'critical';

  return (
    <div
      className={`
        px-4 py-3 hover:bg-gray-50 active:bg-gray-100 cursor-pointer transition-colors group
        ${!alert.read ? 'bg-blue-50/50' : ''}
      `}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <div
          className={`
            p-2 rounded-lg flex-shrink-0
            ${isCritical ? 'bg-red-100' : 'bg-yellow-100'}
          `}
        >
          {isCritical ? (
            <AlertCircle size={18} className="text-red-600" />
          ) : (
            <AlertTriangle size={18} className="text-yellow-600" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 truncate text-sm sm:text-base">
              {alert.productName}
            </span>
            {!alert.read && (
              <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0" />
            )}
          </div>
          <p className="text-xs sm:text-sm text-gray-600 truncate">
            {isCritical ? 'Stock critique' : 'Stock faible'}: {alert.currentStock} {alert.unit}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {formatTime(alert.createdAt)} • Seuil: {alert.threshold} {alert.unit}
          </p>
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className="p-1.5 hover:bg-gray-200 rounded-lg opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0"
          title="Ignorer"
        >
          <X size={14} className="text-gray-400" />
        </button>
      </div>
    </div>
  );
};
